/**
 * AI Drafts - Review queue for AI-generated responses pending human approval
 */

import { useState } from 'react';
import { Check, X, Edit2, RefreshCw, Bot } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import {
  useAIDrafts,
  useApproveAndSendAIDraft,
  useRejectAIDraft,
  useBulkApproveAIDrafts,
  useBulkRejectAIDrafts,
} from '@/lib/api/hooks/useAIDrafts';
import type { AIDraft, DraftClassification, ConversationMessage } from '@/types/aiDraft';

// ─── Classification badge ─────────────────────────────────────────────────────

const classificationConfig: Record<
  DraftClassification,
  { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }
> = {
  auto_send: { label: 'Auto Send', variant: 'default' },
  needs_review: { label: 'Needs Review', variant: 'secondary' },
  escalate: { label: 'Escalate', variant: 'destructive' },
};

function ClassificationBadge({ classification }: { classification: DraftClassification }) {
  const config = classificationConfig[classification] ?? {
    label: classification,
    variant: 'outline' as const,
  };
  return <Badge variant={config.variant}>{config.label}</Badge>;
}

// ─── Conversation context ─────────────────────────────────────────────────────

function ConversationContext({ messages }: { messages: ConversationMessage[] }) {
  if (messages.length === 0) {
    return (
      <p className="text-xs text-muted-foreground italic">No conversation context available.</p>
    );
  }

  return (
    <div className="space-y-1">
      {messages.map((msg) => (
        <div
          key={msg.id}
          className={`text-xs px-2 py-1 rounded ${
            msg.direction === 'inbound'
              ? 'bg-muted text-muted-foreground'
              : 'bg-primary/10 text-foreground ml-4'
          }`}
        >
          <span className="font-medium">
            {msg.direction === 'inbound'
              ? msg.sender_display_name || msg.sender_handle || 'Them'
              : 'You'}
            :{' '}
          </span>
          {msg.content}
        </div>
      ))}
    </div>
  );
}

// ─── Draft card ───────────────────────────────────────────────────────────────

interface DraftCardProps {
  draft: AIDraft;
  selected: boolean;
  onSelectChange: (id: string, checked: boolean) => void;
}

function DraftCard({ draft, selected, onSelectChange }: DraftCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(draft.draft_content);
  const [isRejecting, setIsRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const approveAndSend = useApproveAndSendAIDraft();
  const reject = useRejectAIDraft();

  const isApproving = approveAndSend.isPending;
  const isRejectPending = reject.isPending;

  function handleApprove() {
    approveAndSend.mutate(
      { id: draft.id, draftContent: isEditing ? editedContent : undefined },
      {
        onSuccess: () => toast.success('Draft sent successfully'),
        onError: (err) => toast.error(`Failed to send: ${err.message}`),
      }
    );
  }

  function handleRejectSubmit() {
    if (!rejectReason.trim()) {
      toast.error('Please enter a rejection reason');
      return;
    }
    reject.mutate(
      { id: draft.id, reason: rejectReason.trim() },
      {
        onSuccess: () => {
          toast.success('Draft rejected');
          setIsRejecting(false);
          setRejectReason('');
        },
        onError: (err) => toast.error(`Failed to reject: ${err.message}`),
      }
    );
  }

  const recentMessages = draft.recent_messages ?? [];
  const relativeTime = new Date(draft.created_at).toLocaleString();

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Checkbox
              checked={selected}
              onCheckedChange={(v) => onSelectChange(draft.id, v === true)}
              aria-label="Select draft"
            />
            <ClassificationBadge classification={draft.classification} />
            {draft.channel && (
              <Badge variant="outline" className="text-xs">
                {draft.channel}
              </Badge>
            )}
          </div>
          <span className="text-xs text-muted-foreground">{relativeTime}</span>
        </div>
        {draft.classification_reason && (
          <p className="text-xs text-muted-foreground mt-1">
            <span className="font-medium">Reason:</span> {draft.classification_reason}
          </p>
        )}
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Conversation context */}
        <div>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
            Conversation Context
          </p>
          <ConversationContext messages={recentMessages} />
        </div>

        {/* AI Draft */}
        <div>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
            AI Draft
          </p>
          {isEditing ? (
            <Textarea
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              rows={4}
              className="text-sm"
            />
          ) : (
            <p className="text-sm border rounded-md px-3 py-2 bg-muted/30 whitespace-pre-wrap">
              {draft.draft_content}
            </p>
          )}
        </div>

        {/* Rejection reason input */}
        {isRejecting && (
          <div className="flex gap-2">
            <Input
              placeholder="Rejection reason..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleRejectSubmit();
                if (e.key === 'Escape') {
                  setIsRejecting(false);
                  setRejectReason('');
                }
              }}
              className="text-sm"
              autoFocus
            />
            <Button
              size="sm"
              variant="destructive"
              onClick={handleRejectSubmit}
              disabled={isRejectPending}
            >
              {isRejectPending ? 'Rejecting…' : 'Submit'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setIsRejecting(false);
                setRejectReason('');
              }}
            >
              Cancel
            </Button>
          </div>
        )}

        {/* Actions */}
        {!isRejecting && (
          <div className="flex gap-2 pt-1">
            <Button
              size="sm"
              onClick={handleApprove}
              disabled={isApproving}
            >
              <Check className="h-4 w-4 mr-1" />
              {isApproving ? 'Sending…' : isEditing ? 'Approve & Send' : 'Approve'}
            </Button>

            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                if (isEditing) {
                  // Done editing - revert toggle, keep content
                  setIsEditing(false);
                } else {
                  setIsEditing(true);
                }
              }}
            >
              <Edit2 className="h-4 w-4 mr-1" />
              {isEditing ? 'Done Editing' : 'Edit'}
            </Button>

            <Button
              size="sm"
              variant="ghost"
              className="text-destructive hover:text-destructive"
              onClick={() => setIsRejecting(true)}
              disabled={isApproving}
            >
              <X className="h-4 w-4 mr-1" />
              Reject
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function AIDraftsPage() {
  const { data, isLoading, error, refetch, isFetching } = useAIDrafts('pending');

  const drafts = data?.drafts ?? [];
  const count = data?.count ?? 0;

  // R3c multi-select + bulk actions (bulk approve calls ONLY the approve endpoint
  // - never send; the per-message human send gate is preserved).
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const bulkApprove = useBulkApproveAIDrafts();
  const bulkReject = useBulkRejectAIDrafts();

  const handleSelectChange = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());
  const selectedCount = selectedIds.size;
  const isBulkPending = bulkApprove.isPending || bulkReject.isPending;

  const handleBulkApprove = () => {
    const ids = Array.from(selectedIds);
    bulkApprove.mutate(
      { ids },
      {
        onSuccess: ({ approved, failed }) => {
          if (approved.length)
            toast.success(`${approved.length} draft${approved.length === 1 ? '' : 's'} approved (not sent)`);
          if (failed.length) toast.error(`${failed.length} failed to approve`);
          clearSelection();
        },
        onError: (err) => toast.error(`Bulk approve failed: ${err.message}`),
      }
    );
  };

  const handleBulkReject = () => {
    const ids = Array.from(selectedIds);
    bulkReject.mutate(
      { ids, reason: 'Bulk rejected' },
      {
        onSuccess: ({ rejected, failed }) => {
          if (rejected.length)
            toast.success(`${rejected.length} draft${rejected.length === 1 ? '' : 's'} rejected`);
          if (failed.length) toast.error(`${failed.length} failed to reject`);
          clearSelection();
        },
        onError: (err) => toast.error(`Bulk reject failed: ${err.message}`),
      }
    );
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Bot className="h-6 w-6 text-muted-foreground" />
            <div>
              <h1 className="text-2xl font-heading font-bold tracking-tight">AI Drafts</h1>
              <p className="text-sm text-muted-foreground">
                Review and approve AI-generated responses before they are sent
              </p>
            </div>
            {!isLoading && (
              <Badge variant="secondary" className="ml-2">
                {count} pending
              </Badge>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {isLoading ? (
          <div className="space-y-4 max-w-2xl">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-48 w-full" />
            ))}
          </div>
        ) : error ? (
          <ErrorState
            className="max-w-md"
            title="Failed to load drafts"
            message={error.message}
            onRetry={() => refetch()}
          />
        ) : drafts.length === 0 ? (
          <EmptyState
            className="max-w-md"
            icon={Bot}
            title="No pending drafts"
            description="AI-generated responses waiting for review will appear here."
          />
        ) : (
          <div className="space-y-4 max-w-2xl">
            {/* R3c bulk-action bar - appears when at least one draft is selected.
                Bulk Approve calls the approve endpoint ONLY (never send). */}
            {selectedCount > 0 && (
              <div
                className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-card px-4 py-3 shadow-sm"
                data-testid="bulk-action-bar"
              >
                <span className="text-sm font-medium">
                  {selectedCount} selected
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={handleBulkApprove}
                    disabled={isBulkPending}
                  >
                    <Check className="h-4 w-4 mr-2" />
                    Approve (not send)
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleBulkReject}
                    disabled={isBulkPending}
                  >
                    <X className="h-4 w-4 mr-2" />
                    Reject
                  </Button>
                  <Button size="sm" variant="ghost" onClick={clearSelection} disabled={isBulkPending}>
                    Clear
                  </Button>
                </div>
              </div>
            )}
            {drafts.map((draft) => (
              <DraftCard
                key={draft.id}
                draft={draft}
                selected={selectedIds.has(draft.id)}
                onSelectChange={handleSelectChange}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
